import { useState, useContext, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';

import PersonOutlineIcon from '@mui/icons-material/PersonOutlineRounded';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import BarChartIcon from '@mui/icons-material/BarChartRounded';

import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import WarningAmberIcon from '@mui/icons-material/WarningAmberRounded';

import { AuthContext } from "../../contexts/auth";
import api from "../../services/api";
import PricingModal from "../../components/pricingModal";
import PayPerUseModal from "../../components/payPerUseModal";
import ChangePasswordModal from "../../components/changePasswordModal";
import Switch from '@mui/material/Switch';

const SectionCard = ({ children, sx = {} }) => (
    <Box sx={{
        background: '#FFFFFF',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '16px',
        p: 3,
        ...sx,
    }}>
        {children}
    </Box>
);

const SectionTitle = ({ icon, title, subtitle }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            background: 'rgba(112,18,128,0.10)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        }}>
            {icon}
        </Box>
        <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1C1C28', lineHeight: 1.2 }}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: '0.75rem', color: '#6B6B7B', mt: 0.2 }}>{subtitle}</Typography>}
        </Box>
    </Box>
);

const UsageStat = ({ label, desc, used, total, color = '#701280' }) => {
    const cappedUsed = total > 0 ? Math.min(used, total) : used;
    const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
    const isHigh = pct >= 80;
    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: desc ? 0.2 : 0.7 }}>
                <Typography sx={{ fontSize: '0.82rem', color: '#4A4A5A', fontWeight: 600 }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: isHigh ? '#EF4444' : '#6B6B7B', fontWeight: 600 }}>
                    {cappedUsed?.toLocaleString()} / {total === 999999 ? '∞' : total?.toLocaleString()}
                </Typography>
            </Box>
            {desc && (
                <Typography sx={{ fontSize: '0.72rem', color: '#9B9BA8', mb: 0.6 }}>{desc}</Typography>
            )}
            <LinearProgress
                variant="determinate"
                value={total === 999999 ? 5 : pct}
                sx={{
                    height: 5,
                    borderRadius: 3,
                    backgroundColor: 'rgba(0,0,0,0.07)',
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        background: isHigh
                            ? 'linear-gradient(90deg, #EF4444, #FF6B35)'
                            : `linear-gradient(90deg, ${color}, ${color}CC)`,
                    },
                }}
            />
        </Box>
    );
};

const ProfilePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, setUser, signOut } = useContext(AuthContext);

    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [profession, setProfession] = useState('');
    const [company, setCompany] = useState('');

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [changePwdOpen, setChangePwdOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [pricingModalOpen, setPricingModalOpen] = useState(false);
    const [payPerUseModalOpen, setPayPerUseModalOpen] = useState(false);
    const [payPerUseEnabled, setPayPerUseEnabled] = useState(false);
    const [payPerUseEurosSpent, setPayPerUseEurosSpent] = useState(0);
    const [payPerUseCostImages, setPayPerUseCostImages] = useState(0);
    const [payPerUseCostChars, setPayPerUseCostChars] = useState(0);
    const [payPerUseCostVideo, setPayPerUseCostVideo] = useState(0);
    const [extraImages, setExtraImages] = useState(0);
    const [extraChars, setExtraChars] = useState(0);
    const [extraVideoSeconds, setExtraVideoSeconds] = useState(0);
    const [hasStripeCustomer, setHasStripeCustomer] = useState(false);
    const [hasRealPaymentMethod, setHasRealPaymentMethod] = useState(false);
    const [stripePlan, setStripePlan] = useState(null);
    const [videoSecondsUsed, setVideoSecondsUsed] = useState(0);
    const [videoSecondsLimit, setVideoSecondsLimit] = useState(0);

    const [billingData, setBillingData] = useState({ name: '', vat_number: '', fiscal_code: '', country: '', region: '', province: '', city: '', adress: '', cap: '', unique_code: '', email_pec: '' });
    const [billingEditing, setBillingEditing] = useState(false);
    const [billingSaving, setBillingSaving] = useState(false);

    const [invoices, setInvoices] = useState([]);

    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    useEffect(() => {
        if (user && !editing) {
            setFirstName(user.first_name || '');
            setLastName(user.last_name || '');
            setEmail(user.email || '');
            setProfession(user.profession || '');
            setCompany(user.company || '');
        }
    }, [user, editing]);

    const fetchStripeStatus = () => {
        api.get('/billing/stripe/subscription/').then(r => {
            setPayPerUseEnabled(r.data?.pay_per_use_enabled || false);
            setPayPerUseEurosSpent(r.data?.pay_per_use_euros_spent || 0);
            setPayPerUseCostImages(r.data?.pay_per_use_cost_images || 0);
            setPayPerUseCostChars(r.data?.pay_per_use_cost_chars || 0);
            setPayPerUseCostVideo(r.data?.pay_per_use_cost_video || 0);
            setExtraImages(r.data?.pay_per_use_extra_images || 0);
            setExtraChars(r.data?.pay_per_use_extra_chars || 0);
            setExtraVideoSeconds(r.data?.pay_per_use_extra_video_seconds || 0);
            setHasStripeCustomer(r.data?.has_stripe_customer || false);
            setHasRealPaymentMethod(r.data?.has_real_payment_method || false);
            setStripePlan(r.data?.plan || null);
            setVideoSecondsUsed(r.data?.video_seconds_used || 0);
            setVideoSecondsLimit(r.data?.video_seconds_limit || 0);
        }).catch(() => {});
    };

    const fetchUserUsage = () => {
        if (setUser) setUser();
    };

    useEffect(() => {
        fetchStripeStatus();
        fetchUserUsage();

        // Handle return from Stripe PPU setup (legacy users adding payment method)
        const params = new URLSearchParams(window.location.search);
        if (params.get('ppu') === 'setup_complete') {
            setSnack({ open: true, severity: 'success', msg: t('profile.ppuSetupComplete') });
            window.history.replaceState({}, '', window.location.pathname);
        }

        const intervalId = setInterval(() => {
            fetchStripeStatus();
            fetchUserUsage();
        }, 3000);

        const handleGenComplete = () => {
            fetchStripeStatus();
            fetchUserUsage();
        };
        window.addEventListener('admind_generation_complete', handleGenComplete);

        api.get('/billing/billing-data/').then(r => setBillingData(r.data)).catch(() => {});
        api.get('/billing/invoices/').then(r => setInvoices(r.data?.invoices || [])).catch(() => {});

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('admind_generation_complete', handleGenComplete);
        };
    }, []);

    const initials = ((user?.first_name || user?.email || 'U')[0]).toUpperCase();
    const displayName = user?.first_name
        ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`
        : user?.email?.split('@')[0] || 'Utente';
    const planName = user?.subscription || 'Free';
    const isPro = user?.is_pro || false;

    const handleStartEdit = () => setEditing(true);
    const handleCancelEdit = () => {
        setEditing(false);
        setFirstName(user?.first_name || '');
        setLastName(user?.last_name || '');
        setEmail(user?.email || '');
        setProfession(user?.profession || '');
        setCompany(user?.company || '');
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const resp = await api.put('/api/access/user/', {
                first_name: firstName,
                last_name: lastName,
                email,
                profession,
                company,
            });
            if (setUser) setUser(resp.data);
            setEditing(false);
            setSnack({ open: true, msg: '✅ Profilo aggiornato con successo!', severity: 'success' });
        } catch (err) {
            setSnack({ open: true, msg: `❌ Errore: ${err?.response?.data?.detail || 'impossibile salvare'}`, severity: 'error' });
        }
        setSaving(false);
    };

    const handleCancelPlan = async () => {
        setCancelling(true);
        try {
            const res = await api.post('/billing/stripe/cancel/');
            if (res.data?.success) {
                fetchStripeStatus();
                setCancelDialogOpen(false);
                setSnack({ open: true, msg: t('profile.cancelDialog.successMsg'), severity: 'success' });
                setCancelling(false);
                return;
            }
        } catch (err) {
            setSnack({
                open: true,
                msg: `Errore: ${err?.response?.data?.error || 'impossibile aprire il portale Stripe'}`,
                severity: 'error',
            });
        }
        setCancelling(false);
    };

    const handleBillingSave = async () => {
        setBillingSaving(true);
        try {
            const res = await api.put('/billing/billing-data/', billingData);
            setBillingData(res.data);
            setBillingEditing(false);
            setSnack({ open: true, msg: '✅ Dati di fatturazione aggiornati!', severity: 'success' });
        } catch {
            setSnack({ open: true, msg: '❌ Errore nel salvataggio dei dati di fatturazione', severity: 'error' });
        }
        setBillingSaving(false);
    };

    const handleSignOut = () => {
        signOut();
        navigate('/login');
    };

    return (
        <Box sx={{ height: '100%', overflow: 'auto', backgroundColor: '#FAFAF8', p: { xs: 2, md: 4 } }}>
            <Box sx={{ maxWidth: 720, mx: 'auto' }}>

                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#1C1C28', lineHeight: 1.2, letterSpacing: '-0.5px' }}>
                            {t('profile.title')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: '#6B6B7B', mt: 0.4 }}>
                            {t('profile.subtitle')}
                        </Typography>
                    </Box>
                    <Tooltip title={t('profile.logout')}>
                        <IconButton
                            onClick={handleSignOut}
                            sx={{
                                color: '#6B6B7B',
                                border: '1px solid rgba(0,0,0,0.1)',
                                borderRadius: '10px',
                                p: 0.9,
                                '&:hover': { color: '#EF4444', borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.05)' },
                            }}
                        >
                            <LogoutIcon sx={{ fontSize: '1.1rem' }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                <SectionCard sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Avatar sx={{
                            width: 56,
                            height: 56,
                            fontSize: '1.4rem',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #701280, #8E3A9E)',
                            flexShrink: 0,
                        }}>
                            {initials}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1C1C28', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {displayName}
                            </Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: '#6B6B7B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {user?.email}
                            </Typography>
                        </Box>
                        <Chip
                            icon={<WorkspacePremiumIcon sx={{ fontSize: '0.9rem !important' }} />}
                            label={planName}
                            size="small"
                            sx={{
                                background: isPro ? 'linear-gradient(135deg, #70128022, #70128011)' : 'rgba(0,0,0,0.06)',
                                color: isPro ? '#701280' : '#6B6B7B',
                                border: `1px solid ${isPro ? 'rgba(112,18,128,0.25)' : 'rgba(0,0,0,0.1)'}`,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                            }}
                        />
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(0,0,0,0.07)', mb: 3 }} />

                    <SectionTitle
                        icon={<WorkspacePremiumIcon sx={{ fontSize: '1rem', color: '#701280' }} />}
                        title={t('profile.planActive')}
                        subtitle={t('profile.planActiveDesc')}
                    />

                    <UsageStat
                        label={t('profile.aiChars')}
                        desc={t('profile.aiCharsDesc')}
                        used={user?.tokens_used ?? 0}
                        total={user?.tokens ?? 30000}
                        color="#701280"
                    />
                    <UsageStat
                        label={t('profile.aiImages')}
                        desc={isPro ? t('profile.aiImagesDesc') : t('profile.aiImagesDescFree')}
                        used={user?.images_used ?? 0}
                        total={user?.images ?? 1}
                        color="#701280"
                    />

                    {/* Video stat — PRO only, shown in muted grey */}
                    {isPro && (
                        <Box sx={{ mb: 1, opacity: 0.65 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.2 }}>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#6B6B7B' }}>
                                    {t('profile.videoAI')}
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#9B9BA8' }}>
                                    {videoSecondsLimit > 0
                                        ? `${videoSecondsUsed?.toLocaleString()}s / ${videoSecondsLimit?.toLocaleString()}s`
                                        : t('profile.videoPayPerUse')}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.72rem', color: '#9B9BA8', mb: 0.6 }}>{t('profile.videoAIDesc')}</Typography>
                            {videoSecondsLimit > 0 && (
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, (videoSecondsUsed / videoSecondsLimit) * 100)}
                                    sx={{
                                        height: 5,
                                        borderRadius: 3,
                                        backgroundColor: 'rgba(0,0,0,0.07)',
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#9B9BA8', borderRadius: 3 },
                                    }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Pay-per-use toggle (PRO only) */}
                    {isPro && (
                        <Box sx={{ mt: 2, p: 2, background: 'rgba(112,18,128,0.05)', border: '1px solid rgba(112,18,128,0.12)', borderRadius: '12px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#3B3B4B' }}>
                                        {t('profile.payPerUseTitle')}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#6B6B7B', mt: 0.3 }}>
                                        {payPerUseEnabled ? t('profile.payPerUseOn') : t('profile.payPerUseOff')}
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={payPerUseEnabled}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setPayPerUseModalOpen(true);
                                        } else {
                                            api.post('/billing/stripe/pay-per-use/', { enable: false })
                                                .then(() => setPayPerUseEnabled(false))
                                                .catch((err) => {
                                                    const code = err?.response?.data?.error;
                                                    const euros = err?.response?.data?.euros_spent;
                                                    if (code === 'cannot_disable_pending_balance') {
                                                        setSnack({
                                                            open: true,
                                                            severity: 'warning',
                                                            msg: t('profile.payPerUseCannotDisable', { euros: Number(euros).toFixed(2) }),
                                                        });
                                                    }
                                                });
                                        }
                                    }}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#701280' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#701280' },
                                    }}
                                />
                            </Box>
                            {payPerUseEnabled && (
                                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(112,18,128,0.1)' }}>
                                    {[
                                        {
                                            label: t('profile.aiImagesLabel'),
                                            desc: t('profile.extraImages', { count: extraImages }),
                                            cost: payPerUseCostImages,
                                        },
                                        {
                                            label: t('profile.aiCharsLabel'),
                                            desc: t('profile.extraChars', { count: (extraChars / 1000).toFixed(1) }),
                                            cost: payPerUseCostChars,
                                        },
                                        {
                                            label: t('profile.videoAILabel'),
                                            desc: t('profile.extraVideo', { count: extraVideoSeconds }),
                                            cost: payPerUseCostVideo,
                                        },
                                    ].map((row, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 1, borderBottom: i < 2 ? '1px solid rgba(112,18,128,0.07)' : 'none' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3B3B4B' }}>{row.label}</Typography>
                                                <Typography sx={{ fontSize: '0.7rem', color: '#9B9BA8', mt: 0.1 }}>{row.desc}</Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: row.cost > 0 ? '#701280' : '#9B9BA8', flexShrink: 0 }}>
                                                {row.cost > 0 ? `€${Number(row.cost).toFixed(2)}` : '€0,00'}
                                            </Typography>
                                        </Box>
                                    ))}
                                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(112,18,128,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#18181B' }}>{t('profile.totalExtra')}</Typography>
                                            <Typography sx={{ fontSize: '0.7rem', color: '#9B9BA8', mt: 0.1 }}>{t('profile.totalExtraDesc')}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: payPerUseEurosSpent > 0 ? '#701280' : '#9B9BA8' }}>
                                            {payPerUseEurosSpent > 0 ? `€${Number(payPerUseEurosSpent).toFixed(2)}` : '€0,00'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}

                    <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {!isPro ? (
                            <Button
                                variant="contained"
                                startIcon={<WorkspacePremiumIcon />}
                                onClick={() => setPricingModalOpen(true)}
                                sx={{
                                    background: 'linear-gradient(135deg, #701280, #8E3A9E)',
                                    borderRadius: '10px',
                                    fontWeight: 600,
                                    px: 2.5,
                                    '&:hover': { background: 'linear-gradient(135deg, #5535DD, #701280)', boxShadow: '0 4px 20px rgba(112,18,128,0.3)' },
                                }}
                            >
                                {t('profile.upgradePro')}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<WorkspacePremiumIcon />}
                                    onClick={() => setPricingModalOpen(true)}
                                    sx={{
                                        borderColor: 'rgba(112,18,128,0.3)',
                                        color: '#701280',
                                        borderRadius: '10px',
                                        fontWeight: 600,
                                        px: 2.5,
                                        '&:hover': { borderColor: '#701280', background: 'rgba(112,18,128,0.05)' },
                                    }}
                                >
                                    {t('profile.changePlan')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<WarningAmberIcon />}
                                    onClick={() => setCancelDialogOpen(true)}
                                    sx={{
                                        borderColor: 'rgba(239,68,68,0.3)',
                                        color: '#EF4444',
                                        borderRadius: '10px',
                                        fontWeight: 600,
                                        px: 2.5,
                                        '&:hover': { borderColor: '#EF4444', background: 'rgba(239,68,68,0.05)' },
                                    }}
                                >
                                    {t('profile.cancelPlan')}
                                </Button>
                            </>
                        )}
                    </Box>
                </SectionCard>


                <SectionCard sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }}>
                        <SectionTitle
                            icon={<PersonOutlineIcon sx={{ fontSize: '1rem', color: '#701280' }} />}
                            title={t("profile.personalData")}
                            subtitle={t("profile.personalDataDesc")}
                        />
                        {!editing && (
                            <IconButton
                                size="small"
                                onClick={handleStartEdit}
                                sx={{
                                    color: '#701280',
                                    border: '1px solid rgba(112,18,128,0.2)',
                                    borderRadius: '8px',
                                    p: 0.7,
                                    '&:hover': { background: 'rgba(112,18,128,0.06)' },
                                }}
                            >
                                <EditIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                        )}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            label={t("profile.firstName")}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            size="small"
                            disabled={!editing}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                        <TextField
                            label={t("profile.lastName")}
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            size="small"
                            disabled={!editing}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                        <TextField
                            label={t("profile.email")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            size="small"
                            disabled={!editing}
                            type="email"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                        <TextField
                            label={t("profile.profession")}
                            value={profession}
                            onChange={(e) => setProfession(e.target.value)}
                            size="small"
                            disabled={!editing}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                        <TextField
                            label={t("profile.company")}
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            size="small"
                            disabled={!editing}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' }, gridColumn: { sm: '1 / -1' } }}
                        />
                    </Box>

                    {editing && (
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5 }}>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
                                sx={{
                                    background: 'linear-gradient(135deg, #701280, #8E3A9E)',
                                    borderRadius: '10px',
                                    fontWeight: 600,
                                    px: 2.5,
                                    '&:hover': { background: 'linear-gradient(135deg, #5535DD, #701280)' },
                                }}
                            >
                                {saving ? t("profile.saving") : t("profile.saveChanges")}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleCancelEdit}
                                disabled={saving}
                                sx={{
                                    borderColor: 'rgba(0,0,0,0.15)',
                                    color: '#6B6B7B',
                                    borderRadius: '10px',
                                    '&:hover': { borderColor: 'rgba(0,0,0,0.3)', background: 'rgba(0,0,0,0.03)' },
                                }}
                            >
                                Annulla
                            </Button>
                        </Box>
                    )}
                </SectionCard>

                {/* ── Dati di fatturazione ── */}
                <SectionCard sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }}>
                        <SectionTitle
                            icon={<BusinessIcon sx={{ fontSize: '1rem', color: '#701280' }} />}
                            title={t("profile.billingData")}
                            subtitle={t("profile.billingDataDesc")}
                        />
                        {!billingEditing && (
                            <IconButton size="small" onClick={() => setBillingEditing(true)} sx={{ color: '#701280', border: '1px solid rgba(112,18,128,0.2)', borderRadius: '8px', p: 0.7, '&:hover': { background: 'rgba(112,18,128,0.06)' } }}>
                                <EditIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                        )}
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        {[
                            { label: 'Ragione Sociale / Nome', key: 'name' },
                            { label: 'P.IVA / VAT Number', key: 'vat_number' },
                            { label: 'Codice Fiscale', key: 'fiscal_code' },
                            { label: 'Codice SDI', key: 'unique_code' },
                            { label: 'PEC', key: 'email_pec' },
                            { label: 'Nazione', key: 'country' },
                            { label: 'Indirizzo', key: 'adress' },
                            { label: 'CAP', key: 'cap' },
                            { label: 'Città', key: 'city' },
                            { label: 'Provincia', key: 'province' },
                        ].map(({ label, key }) => (
                            <TextField
                                key={key}
                                label={label}
                                value={billingData[key] || ''}
                                onChange={(e) => setBillingData(d => ({ ...d, [key]: e.target.value }))}
                                size="small"
                                disabled={!billingEditing}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                        ))}
                    </Box>
                    {billingEditing && (
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5 }}>
                            <Button variant="contained" onClick={handleBillingSave} disabled={billingSaving} startIcon={billingSaving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />} sx={{ background: 'linear-gradient(135deg, #701280, #8E3A9E)', borderRadius: '10px', fontWeight: 600, px: 2.5, '&:hover': { background: 'linear-gradient(135deg, #5535DD, #701280)' } }}>
                                {billingSaving ? t("profile.saving") : t("profile.save")}
                            </Button>
                            <Button variant="outlined" onClick={() => setBillingEditing(false)} disabled={billingSaving} sx={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6B6B7B', borderRadius: '10px', '&:hover': { borderColor: 'rgba(0,0,0,0.3)', background: 'rgba(0,0,0,0.03)' } }}>
                                Annulla
                            </Button>
                        </Box>
                    )}
                </SectionCard>

                {/* ── Fatture ── */}
                <SectionCard sx={{ mb: 3 }}>
                    <SectionTitle
                        icon={<ReceiptLongIcon sx={{ fontSize: '1rem', color: '#701280' }} />}
                        title={t("profile.invoices")}
                        subtitle={t("profile.invoicesDesc")}
                    />
                    {invoices.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4, color: '#9B9BA8' }}>
                            <ReceiptLongIcon sx={{ fontSize: '2.5rem', mb: 1, opacity: 0.4 }} />
                            <Typography sx={{ fontSize: '0.85rem', color: '#9B9BA8' }}>{t('profile.noInvoices')}</Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#BABAC8', mt: 0.5 }}>{t('profile.noInvoicesDesc')}</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr 0.6fr', gap: 0, borderBottom: '1px solid rgba(0,0,0,0.08)', pb: 1, mb: 0.5 }}>
                                {[t('profile.invoiceNum'), t('profile.invoiceDate'), t('profile.invoiceAmount'), t('profile.invoiceStatus'), ''].map((h, idx) => (
                                    <Typography key={idx} sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#9B9BA8', textTransform: 'uppercase', letterSpacing: '0.05em', px: 1 }}>{h}</Typography>
                                ))}
                            </Box>
                            {invoices.map((inv, i) => {
                                const isPaid = inv.status === 'paid' || inv.status === 2;
                                const isOpen = inv.status === 'open' || inv.status === 1;
                                const statusLabel = isPaid ? t('profile.invoiceSent') : isOpen ? t('profile.invoicePending') : t('profile.invoiceDraft');
                                const statusBg = isPaid ? 'rgba(34,197,94,0.1)' : isOpen ? 'rgba(251,191,36,0.1)' : 'rgba(0,0,0,0.06)';
                                const statusColor = isPaid ? '#16a34a' : isOpen ? '#b45309' : '#9B9BA8';
                                return (
                                    <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr 0.6fr', py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.05)', alignItems: 'center', '&:last-child': { borderBottom: 'none' } }}>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#3B3B4B', px: 1 }}>{inv.code}</Typography>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#6B6B7B', px: 1 }}>{inv.issue_date}</Typography>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#3B3B4B', px: 1 }}>€{inv.total_amount}</Typography>
                                        <Box sx={{ px: 1 }}>
                                            <Chip label={statusLabel} size="small" sx={{ fontSize: '0.68rem', fontWeight: 600, background: statusBg, color: statusColor, height: 20 }} />
                                        </Box>
                                        <Box sx={{ px: 1 }}>
                                            {inv.pdf_url ? (
                                                <Typography
                                                    component="a"
                                                    href={inv.pdf_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#701280', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                >
                                                    PDF
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </SectionCard>


                <SectionCard>
                    <SectionTitle
                        icon={<LockOutlinedIcon sx={{ fontSize: '1rem', color: '#701280' }} />}
                        title={t("profile.security")}
                        subtitle={t("profile.securityDesc")}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontSize: '0.85rem', color: '#4A4A5A', fontWeight: 500 }}>{t('profile.password')}</Typography>
                            <Typography sx={{ fontSize: '0.78rem', color: '#6B6B7B', mt: 0.2 }}>{t('profile.passwordDesc')}</Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={() => setChangePwdOpen(true)}
                            sx={{
                                borderColor: 'rgba(112,18,128,0.25)',
                                color: '#701280',
                                borderRadius: '10px',
                                fontWeight: 600,
                                '&:hover': { borderColor: '#701280', background: 'rgba(112,18,128,0.05)' },
                            }}
                        >
                            {t('profile.changePassword')}
                        </Button>
                    </Box>
                </SectionCard>

            </Box>

            <ChangePasswordModal open={changePwdOpen} onClose={() => setChangePwdOpen(false)} />

            <Dialog
                open={cancelDialogOpen}
                onClose={() => !cancelling && setCancelDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: '20px', p: 1, maxWidth: 400 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1C1C28', pb: 0.5 }}>
                    {t('profile.cancelDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 52,
                        height: 52,
                        borderRadius: '14px',
                        background: 'rgba(239,68,68,0.08)',
                        mb: 2,
                        mt: 1,
                    }}>
                        <WarningAmberIcon sx={{ color: '#EF4444', fontSize: '1.6rem' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.88rem', color: '#4A4A5A', lineHeight: 1.7 }}>
                        {t('profile.cancelDialog.body', { plan: planName })}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => setCancelDialogOpen(false)}
                        disabled={cancelling}
                        sx={{
                            color: '#6B6B7B',
                            borderRadius: '10px',
                            fontWeight: 600,
                            border: '1px solid rgba(0,0,0,0.12)',
                            px: 2.5,
                        }}
                    >
                        {t('profile.cancelDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleCancelPlan}
                        disabled={cancelling}
                        startIcon={cancelling ? <CircularProgress size={14} color="inherit" /> : <WarningAmberIcon />}
                        sx={{
                            background: 'rgba(239,68,68,0.9)',
                            color: '#fff',
                            borderRadius: '10px',
                            fontWeight: 600,
                            px: 2.5,
                            '&:hover': { background: '#DC2626' },
                        }}
                    >
                        {cancelling ? t("profile.cancelDialog.cancelling") : t("profile.cancelDialog.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={5000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: '12px' }}>
                    {snack.msg}
                </Alert>
            </Snackbar>

            <PricingModal
                open={pricingModalOpen}
                onClose={() => setPricingModalOpen(false)}
                currentPlan={stripePlan}
                onSuccess={(planLabel) => {
                    setUser();
                    fetchStripeStatus();
                    setSnack({ open: true, msg: `✅ Piano ${planLabel} attivato con successo!`, severity: 'success' });
                }}
            />

            <PayPerUseModal
                open={payPerUseModalOpen}
                onClose={() => setPayPerUseModalOpen(false)}
                isPro={isPro}
                hasStripeCustomer={hasStripeCustomer}
                hasRealPaymentMethod={hasRealPaymentMethod}
                onActivated={() => { setPayPerUseEnabled(true); fetchStripeStatus(); }}
            />
        </Box>
    );
};

export default ProfilePage;
